// Report charts (owner, 2026-10-22: "build Reports with attractive charts"): the trend chart (area or
// bars, hover/keyboard tooltip, the previous period as a dashed line), a donut with its legend, ranked
// bars, a ring gauge, a stacked meter and the shopping funnel. Drawn as SVG at their real width so the
// axis text stays at the type scale; colours are the logo gradient and the chart tokens.
import { useId, useState, type ReactNode } from "react";
import { cx } from "@ady/ui";
import { niceStep, useWidth } from "./Charts";

export const CHART_COLOURS = [
  "var(--ady-color-chart-1)",
  "var(--ady-color-chart-3)",
  "var(--ady-color-chart-2)",
  "var(--ady-color-chart-4)",
  "var(--ady-color-chart-5)",
  "var(--ady-color-chart-6)",
  "var(--ady-color-border-default)",
];

/** 950 · 12.5k · 1.2L · 3.4Cr (Nepali lakh/crore) for axis ticks. */
export function compact(v: number): string {
  const a = Math.abs(v);
  const s = v < 0 ? "-" : "";
  const n = (x: number) => (x >= 10 ? Math.round(x) : Math.round(x * 10) / 10).toString();
  if (a >= 1e7) return `${s}${n(a / 1e7)}Cr`;
  if (a >= 1e5) return `${s}${n(a / 1e5)}L`;
  if (a >= 1e3) return `${s}${n(a / 1e3)}k`;
  return `${s}${Math.round(a * 10) / 10}`;
}

export interface TrendPoint {
  /** Axis label ("Oct 3"). */
  label: string;
  /** Tooltip heading ("Fri, 3 Oct 2026" or "Week of 29 Sept"). */
  title: string;
  value: number;
  /** The same position in the previous period. */
  prev?: number;
}

/** The trend: an area (many points) or bars (few), with a tooltip that follows the pointer or keys. */
export function TrendChart({
  points,
  kind,
  format,
  label,
  seriesLabel,
  prevLabel,
  height = 260,
}: {
  points: TrendPoint[];
  kind: "area" | "bars";
  /** Tooltip value (money with Rs, counts, %). */
  format: (v: number) => string;
  label: string;
  seriesLabel: string;
  prevLabel?: string;
  height?: number;
}) {
  const [box, width] = useWidth<HTMLDivElement>(720);
  const [hover, setHover] = useState<number | null>(null);
  const gid = `trend${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const w = width;
  const h = height;
  const top = 12;
  const bottom = 26;
  const values = points.flatMap((p) => [p.value, p.prev ?? 0]);
  const lo = Math.min(0, ...values);
  const hi = Math.max(1, ...values);
  const step = niceStep(hi - lo);
  const yMax = Math.ceil(hi / step) * step;
  const yMin = lo < 0 ? Math.floor(lo / step) * step : 0;
  const ticks: number[] = [];
  for (let v = yMin; v <= yMax + step / 2; v += step) ticks.push(v);
  const left = 10 + Math.max(...ticks.map((v) => compact(v).length)) * 7;
  const right = 10;
  const plotW = Math.max(10, w - left - right);
  const plotH = h - top - bottom;
  const n = Math.max(1, points.length);
  const bars = kind === "bars";
  const slot = plotW / n;
  const x = (i: number) =>
    bars || n === 1 ? left + slot * i + slot / 2 : left + (i * plotW) / (n - 1);
  const y = (v: number) => top + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH;
  const path = (get: (p: TrendPoint) => number) =>
    points.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(get(p)).toFixed(1)}`).join(" ");
  const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(plotW / 70))));
  const hasPrev = points.some((p) => p.prev !== undefined);
  const pick = (clientX: number, rect: DOMRect) => {
    const px = ((clientX - rect.left) / rect.width) * w;
    const i =
      bars || n === 1
        ? Math.floor((px - left) / slot)
        : Math.round(((px - left) / plotW) * (n - 1));
    setHover(Math.min(n - 1, Math.max(0, i)));
  };
  const at = hover !== null ? points[hover] : undefined;
  const barW = Math.max(3, Math.min(34, slot * 0.62));
  return (
    <div ref={box} className="relative">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="h-auto w-full touch-pan-y outline-none"
        role="img"
        aria-label={label}
        tabIndex={0}
        onPointerMove={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect())}
        onPointerLeave={() => setHover(null)}
        onBlur={() => setHover(null)}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") setHover((i) => Math.min(n - 1, (i ?? -1) + 1));
          else if (e.key === "ArrowLeft") setHover((i) => Math.max(0, (i ?? n) - 1));
          else return;
          e.preventDefault();
        }}
      >
        <defs>
          <linearGradient
            id={`${gid}s`}
            gradientUnits="userSpaceOnUse"
            x1={left}
            x2={w - right}
            y1="0"
            y2="0"
          >
            <stop offset="0" stopColor="var(--ady-color-brand-blue)" />
            <stop offset="1" stopColor="var(--ady-color-brand-green)" />
          </linearGradient>
          <linearGradient id={`${gid}f`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="var(--ady-color-brand-blue)" stopOpacity="0.22" />
            <stop offset="1" stopColor="var(--ady-color-brand-green)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {ticks.map((v) => (
          <g key={v}>
            <line
              x1={left}
              x2={w - right}
              y1={y(v)}
              y2={y(v)}
              stroke="var(--ady-color-border-subtle)"
              strokeDasharray={v === 0 ? undefined : "3 4"}
            />
            <text x={left - 8} y={y(v) + 4} textAnchor="end" className="fill-fg-tertiary text-2xs">
              {compact(v)}
            </text>
          </g>
        ))}
        {hover !== null ? (
          bars ? (
            <rect
              x={left + slot * hover}
              y={top}
              width={slot}
              height={plotH}
              rx={4}
              fill="var(--ady-color-bg-hover)"
            />
          ) : (
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={top}
              y2={top + plotH}
              stroke="var(--ady-color-border-default)"
              strokeDasharray="3 3"
            />
          )
        ) : null}
        {bars ? (
          points.map((p, i) => (
            <g key={i}>
              {p.prev !== undefined ? (
                <rect
                  x={x(i) - barW / 2 - 3}
                  y={Math.min(y(p.prev), y(0))}
                  width={barW}
                  height={Math.abs(y(0) - y(p.prev))}
                  rx={3}
                  fill="var(--ady-color-border-subtle)"
                />
              ) : null}
              <rect
                x={x(i) - barW / 2 + (p.prev !== undefined ? 3 : 0)}
                y={Math.min(y(p.value), y(0))}
                width={barW}
                height={Math.max(p.value ? 2 : 0, Math.abs(y(0) - y(p.value)))}
                rx={3}
                fill={`url(#${gid}s)`}
                opacity={hover === null || hover === i ? 1 : 0.55}
              />
            </g>
          ))
        ) : (
          <>
            {hasPrev ? (
              <path
                d={path((p) => p.prev ?? 0)}
                fill="none"
                stroke="var(--ady-color-text-tertiary)"
                strokeWidth={1.5}
                strokeDasharray="5 5"
              />
            ) : null}
            <path
              d={`${path((p) => p.value)} L${x(n - 1).toFixed(1)} ${y(Math.max(0, yMin))} L${x(0).toFixed(1)} ${y(Math.max(0, yMin))} Z`}
              fill={`url(#${gid}f)`}
            />
            <path
              d={path((p) => p.value)}
              fill="none"
              stroke={`url(#${gid}s)`}
              strokeWidth={2.5}
              strokeLinejoin="round"
            />
            {at ? (
              <circle
                cx={x(hover!)}
                cy={y(at.value)}
                r={5}
                fill="var(--ady-color-bg-surface)"
                stroke="var(--ady-color-brand-blue)"
                strokeWidth={2.5}
              />
            ) : null}
          </>
        )}
        {points.map((p, i) =>
          // Every `every`th label, and the last one (the one before it gives way when too close).
          (i % every === 0 && (n - 1 - i >= every * 0.7 || i === n - 1)) || i === n - 1 ? (
            <text
              key={i}
              x={x(i)}
              y={h - 8}
              textAnchor={
                i === n - 1 && n > 1 && !bars ? "end" : i === 0 && !bars ? "start" : "middle"
              }
              className="fill-fg-tertiary text-2xs"
            >
              {p.label}
            </text>
          ) : null,
        )}
      </svg>
      {at ? (
        <div
          role="status"
          className="pointer-events-none absolute top-1 z-10 flex min-w-36 flex-col gap-0.5 rounded-sm border border-subtle bg-overlay px-3 py-2 text-xs whitespace-nowrap shadow-2"
          style={{
            left: `${(x(hover!) / w) * 100}%`,
            transform: x(hover!) > w * 0.6 ? "translateX(calc(-100% - 12px))" : "translateX(12px)",
          }}
        >
          <span className="font-semibold text-fg">{at.title}</span>
          <span className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-fg-secondary">
              <span className="ady-bg-brand size-2 rounded-full" />
              {seriesLabel}
            </span>
            <span className="ady-ledger font-semibold">{format(at.value)}</span>
          </span>
          {at.prev !== undefined && prevLabel ? (
            <span className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5 text-fg-tertiary">
                <span
                  className="size-2 rounded-full"
                  style={{ background: "var(--ady-color-border-default)" }}
                />
                {prevLabel}
              </span>
              <span className="ady-ledger text-fg-secondary">{format(at.prev)}</span>
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export interface Slice {
  key: string;
  label: string;
  value: number;
  display: string;
}

/** A donut with its legend; hovering a slice or a legend row shows it in the middle. */
export function Donut({
  slices,
  total,
  centerLabel,
  label,
}: {
  slices: Slice[];
  /** What the middle shows when nothing is hovered. */
  total: string;
  centerLabel: string;
  label: string;
}) {
  const [on, setOn] = useState<number | null>(null);
  const sum = slices.reduce((a, s) => a + Math.max(0, s.value), 0) || 1;
  const r = 52;
  const c = 2 * Math.PI * r;
  let acc = 0;
  const cur = on !== null ? slices[on] : undefined;
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative size-40 flex-none">
        <svg viewBox="0 0 140 140" className="size-full -rotate-90" role="img" aria-label={label}>
          <circle
            cx={70}
            cy={70}
            r={r}
            fill="none"
            stroke="var(--ady-color-bg-sunken)"
            strokeWidth={18}
          />
          {slices.map((s, i) => {
            const len = (Math.max(0, s.value) / sum) * c;
            const off = acc;
            acc += len;
            return (
              <circle
                key={s.key}
                cx={70}
                cy={70}
                r={r}
                fill="none"
                stroke={CHART_COLOURS[i % CHART_COLOURS.length]}
                strokeWidth={on === i ? 22 : 18}
                strokeDasharray={`${Math.max(0, len - (slices.length > 1 ? 1.5 : 0))} ${c}`}
                strokeDashoffset={-off}
                opacity={on === null || on === i ? 1 : 0.35}
                onPointerEnter={() => setOn(i)}
                onPointerLeave={() => setOn(null)}
                className="transition-opacity"
              />
            );
          })}
        </svg>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          <span className="line-clamp-2 text-2xs text-fg-tertiary">
            {cur ? cur.label : centerLabel}
          </span>
          <span className="ady-ledger text-sm font-bold">{cur ? cur.display : total}</span>
          {cur ? (
            <span className="text-2xs font-semibold text-link">
              {Math.round((Math.max(0, cur.value) / sum) * 1000) / 10}%
            </span>
          ) : null}
        </div>
      </div>
      <ul className="flex w-full flex-col gap-1">
        {slices.map((s, i) => (
          <li
            key={s.key}
            onPointerEnter={() => setOn(i)}
            onPointerLeave={() => setOn(null)}
            className={cx(
              "flex items-center gap-2 rounded-xs px-1.5 py-1 text-xs",
              on === i && "bg-hover",
            )}
          >
            <span
              className="size-2.5 flex-none rounded-full"
              style={{ background: CHART_COLOURS[i % CHART_COLOURS.length] }}
            />
            <span className="ady-user-content min-w-0 flex-1 truncate">{s.label}</span>
            <span className="text-fg-tertiary">
              {Math.round((Math.max(0, s.value) / sum) * 1000) / 10}%
            </span>
            <span className="ady-ledger w-24 text-end font-semibold">{s.display}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Ranked bars: name, value, share, a gradient bar. */
export function BarList({
  rows,
  label,
}: {
  rows: { key: string; label: string; value: number; display: string; note?: ReactNode }[];
  label: string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  const sum = rows.reduce((a, r) => a + Math.max(0, r.value), 0) || 1;
  return (
    <ol className="flex flex-col gap-2.5" aria-label={label}>
      {rows.map((r, i) => (
        <li key={r.key} className="flex flex-col gap-1 text-sm">
          <span className="flex items-baseline gap-2">
            <span className="w-4 flex-none text-2xs font-semibold text-fg-tertiary">{i + 1}</span>
            <span className="ady-user-content min-w-0 flex-1 truncate">{r.label}</span>
            {r.note}
            <span className="text-2xs text-fg-tertiary">
              {Math.round((Math.max(0, r.value) / sum) * 1000) / 10}%
            </span>
            <span className="ady-ledger flex-none font-semibold">{r.display}</span>
          </span>
          <span className="ms-6 h-1.5 overflow-hidden rounded-full bg-sunken">
            <span
              className="ady-bg-brand block h-full rounded-full"
              style={{ width: `${(Math.max(0, r.value) / max) * 100}%` }}
            />
          </span>
        </li>
      ))}
    </ol>
  );
}

/** A ring gauge for a rate (0–1). */
export function Ring({
  value,
  display,
  label,
  tone = "brand",
}: {
  value: number;
  display: string;
  label: string;
  tone?: "brand" | "success" | "warning" | "danger";
}) {
  const gid = `ring${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const r = 30;
  const c = 2 * Math.PI * r;
  const v = Math.min(1, Math.max(0, value));
  const stroke =
    tone === "brand"
      ? `url(#${gid})`
      : tone === "success"
        ? "var(--ady-color-tone-success-fg)"
        : tone === "warning"
          ? "var(--ady-color-tone-warning-fg)"
          : "var(--ady-color-tone-danger-fg)";
  return (
    <div className="flex items-center gap-3">
      <div className="relative size-18 flex-none">
        <svg
          viewBox="0 0 76 76"
          className="size-full -rotate-90"
          role="img"
          aria-label={`${label}: ${display}`}
        >
          <defs>
            <linearGradient id={gid} x1="0" x2="1" y1="0" y2="1">
              <stop offset="0" stopColor="var(--ady-color-brand-blue)" />
              <stop offset="1" stopColor="var(--ady-color-brand-green)" />
            </linearGradient>
          </defs>
          <circle
            cx={38}
            cy={38}
            r={r}
            fill="none"
            stroke="var(--ady-color-bg-sunken)"
            strokeWidth={8}
          />
          <circle
            cx={38}
            cy={38}
            r={r}
            fill="none"
            stroke={stroke}
            strokeWidth={8}
            strokeLinecap="round"
            strokeDasharray={`${v * c} ${c}`}
          />
        </svg>
        <span className="ady-ledger absolute inset-0 flex items-center justify-center text-xs font-bold">
          {display}
        </span>
      </div>
      <span className="text-xs font-medium text-fg-secondary">{label}</span>
    </div>
  );
}

/** One bar split into parts (collected / still to collect / refunded), with a legend. */
export function StackMeter({
  parts,
  label,
}: {
  parts: { key: string; label: string; value: number; display: string; colour: string }[];
  label: string;
}) {
  const sum = parts.reduce((a, p) => a + Math.max(0, p.value), 0) || 1;
  return (
    <div className="flex flex-col gap-2.5" role="img" aria-label={label}>
      <div className="flex h-3 overflow-hidden rounded-full bg-sunken">
        {parts.map((p) => (
          <span
            key={p.key}
            style={{ width: `${(Math.max(0, p.value) / sum) * 100}%`, background: p.colour }}
            className="h-full"
          />
        ))}
      </div>
      <ul className="grid gap-1.5 sm:grid-cols-3">
        {parts.map((p) => (
          <li key={p.key} className="flex flex-col">
            <span className="flex items-center gap-1.5 text-2xs text-fg-tertiary">
              <span className="size-2 rounded-full" style={{ background: p.colour }} />
              {p.label} · {Math.round((Math.max(0, p.value) / sum) * 100)}%
            </span>
            <span className="ady-ledger text-sm font-semibold">{p.display}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The shopping funnel: each step as a bar, with the share that went on to the next. */
export function Funnel({
  steps,
  label,
  stepRate,
}: {
  steps: { key: string; label: string; value: number }[];
  label: string;
  /** "64% went on" between two steps. */
  stepRate: (pct: string) => string;
}) {
  const max = Math.max(1, steps[0]?.value ?? 0, ...steps.map((s) => s.value));
  return (
    <ol className="flex flex-col gap-1.5" aria-label={label}>
      {steps.map((s, i) => {
        const next = steps[i + 1];
        return (
          <li key={s.key} className="flex flex-col gap-1">
            <span className="flex items-center gap-3">
              <span className="w-36 flex-none text-xs text-fg-secondary">{s.label}</span>
              <span className="relative h-7 flex-1 overflow-hidden rounded-xs bg-sunken">
                <span
                  className="ady-bg-brand absolute inset-y-0 start-0 rounded-xs"
                  style={{ width: `${(s.value / max) * 100}%`, opacity: 1 - i * 0.12 }}
                />
              </span>
              <span className="ady-ledger w-16 text-end text-sm font-semibold">
                {s.value.toLocaleString("en-IN")}
              </span>
            </span>
            {next ? (
              <span className="ms-39 text-2xs text-fg-tertiary">
                {stepRate(s.value > 0 ? `${Math.round((next.value / s.value) * 1000) / 10}` : "0")}
              </span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
